import type { ApolloCache } from "@apollo/client"
import { Box, Button, HStack, Input, Text, VStack } from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import { flattenEdges } from "@pathwright/utils"
import PanelSection from "@pathwright/ux/src/components/augmentation/AugmentationPanelSection"
import { AugmentationComponent } from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import {
  AUGMENTATION_PANEL_INPUT_PROPS,
  AUGMENTATION_PANEL_SELECT_PROPS,
  AUGMENTATION_PANEL_STATIC_TEXT_PROPS
} from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import CustomSelect from "@pathwright/ux/src/components/form/CustomSelect"
import Field from "@pathwright/ux/src/components/form/Field"
import SelectField from "@pathwright/ux/src/components/form/SelectField"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import Link, { LinkProps } from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import type { FormikHelpers } from "formik"
import { Form, FormikProps, useFormikContext } from "formik"
import { useEffect, useMemo } from "react"
import { useHistory } from "react-router-dom"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  CoverPageOfferFragment as CoverPageOffer,
  CoverPageOfferableCohortsDocument,
  CoverPageOfferableCohortsQuery,
  CoverPageOfferableCohortsQueryVariables,
  CreateGroupMutation,
  GroupFragment,
  OfferKind,
  StoreOfferInput,
  useCoverPageOfferableCohortsQuery,
  useCreateGroupMutation,
  useCreateOfferMutation,
  useGroupQuery,
  useUpdateOfferMutation
} from "@pathwright/graphql-client/src/generated"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageIsEditable } from "../access"
import { COVER_PAGE_PANEL_ROOT_KEY } from "../constants"
import type { CoverPageOfferableCohort } from "../types"
import { CoverPageHashSection } from "../types"
import { useCoverPageTranslate } from "../utils"
import { CoverPagePanelFormActions } from "./CoverPagePanelFormActions"
import CoverPagePanelOfferCreateCohortGate from "./CoverPagePanelOfferCreateCohortGate"
import { CoverPagePanelRichTextField } from "./CoverPagePanelRichTextField"
import {
  formatCoverPageScrollHash,
  useCoverPagePanelErrorMessage
} from "./utils"

export type CoverPageT = ReturnType<typeof useCoverPageTranslate>["t"]

const offerSchema = z.object({
  kind: z.nativeEnum(OfferKind),
  targetCohortId: z.string(),
  title: z.string(),
  highlights: z.string(),
  buttonLabel: z.string().max(25, "Button label must be 25 characters or less"),
  accessLabel: z.string(),
  pricingLabel: z.string(),
  externalUrl: z.string()
})

type CoverPagePanelOfferValues = z.infer<typeof offerSchema>

const NEW_COHORT_TARGET_ID = "new_cohort"
const NEW_COHORT_VISIBILITY = 5

export function resolveOfferCohortTarget({
  cohortCount,
  currentTargetCohortId,
  hasError,
  isLoading
}: {
  cohortCount: number
  currentTargetCohortId: string
  hasError: boolean
  isLoading: boolean
}) {
  const hasNoAvailableCohorts = !isLoading && !hasError && cohortCount === 0

  return {
    hasNoAvailableCohorts,
    targetCohortId: hasNoAvailableCohorts
      ? NEW_COHORT_TARGET_ID
      : currentTargetCohortId
  }
}

function isNewCohortTarget(
  values: Pick<CoverPagePanelOfferValues, "targetCohortId">
) {
  return values.targetCohortId === NEW_COHORT_TARGET_ID
}

export function createOfferValidator(t: CoverPageT) {
  return toFormikValidate(
    offerSchema.superRefine((values, ctx) => {
      if (!values.title.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["title"],
          message: t("panel_offer.title_required", {
            defaultValue: "Enter an offer title."
          })
        })
      }

      if (
        values.kind === OfferKind.JoinCohort &&
        !isNewCohortTarget(values) &&
        !Number(values.targetCohortId)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["targetCohortId"],
          message: t("panel_offer.select_a_cohort", {
            defaultValue: "Select a Cohort."
          })
        })
      }

      if (values.kind === OfferKind.External && !values.externalUrl.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["externalUrl"],
          message: t("panel_offer.enter_a_link", {
            defaultValue: "Enter a link."
          })
        })
      }
    })
  )
}

function addCohortToOfferableCohortsCache(
  cache: ApolloCache<unknown>,
  resourceId: number,
  cohort: NonNullable<CreateGroupMutation["createGroup"]>
) {
  cache.updateQuery<
    CoverPageOfferableCohortsQuery,
    CoverPageOfferableCohortsQueryVariables
  >(
    {
      query: CoverPageOfferableCohortsDocument,
      variables: { resourceId }
    },
    (offerableCohortsData) => {
      if (!offerableCohortsData?.groups) return offerableCohortsData

      if (
        offerableCohortsData.groups.edges.some(
          (edge) => edge.node.id === cohort.id
        )
      ) {
        return offerableCohortsData
      }

      return {
        ...offerableCohortsData,
        groups: {
          ...offerableCohortsData.groups,
          edges: [
            {
              __typename: "GroupEdge" as const,
              node: {
                __typename: "Group" as const,
                id: cohort.id,
                name: cohort.name,
                visibility: cohort.visibility,
                registration_starts: cohort.registration_starts,
                registration_ends: cohort.registration_ends,
                access_starts: cohort.access_starts,
                access_ends: cohort.access_ends,
                price: cohort.price,
                is_subscription_only: cohort.is_subscription_only
              }
            },
            ...offerableCohortsData.groups.edges
          ]
        }
      }
    }
  )
}

function navigateToCoverPageOffersSection(
  scrollToSection: (section?: string | null) => void
) {
  const hash = formatCoverPageScrollHash(CoverPageHashSection.Offers)

  scrollToSection(hash)

  if (typeof window === "undefined") return

  const syncHash = () => {
    const url = new URL(window.location.href)
    url.hash = hash
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    )
    scrollToSection(hash)
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(syncHash)
  })
}

const getOfferKindOptions = (t: CoverPageT) =>
  [
    {
      value: OfferKind.JoinCohort,
      label: t("panel_offer.join_a_cohort", { defaultValue: "Join a Cohort" }),
      icon: "avatar-plus-circle"
    },
    {
      value: OfferKind.CreateCohort,
      label: t("panel_offer.create_private_cohort", {
        defaultValue: "Create Private Cohort"
      }),
      icon: "group"
    }
    // TODO: add external link Offers once we can support them
    // {
    //   value: OfferKind.External,
    //   label: t("panel_offer.external_link", { defaultValue: "External Link" }),
    //   icon: "external-link"
    // }
  ] as Array<{
    value: OfferKind
    label: string
    icon: IconName
  }>

function useCoverPageOffer(offerId?: string) {
  const offers = useCoverPageContext((value) => value.offers)

  return useMemo(
    () => offers?.find((offer) => offer.id === Number(offerId)) || null,
    [offerId, offers]
  )
}

function getCurrentOfferKind({
  offer,
  formKind
}: {
  offer: CoverPageOffer | null
  formKind: OfferKind
}): OfferKind {
  return offer?.kind || formKind
}

function getDefaultButtonLabel(kind: OfferKind, t: CoverPageT) {
  if (kind === OfferKind.CreateCohort)
    return t("panel_offer.create_group", { defaultValue: "Create Group" })
  if (kind === OfferKind.External)
    return t("panel_offer.apply_now", { defaultValue: "Apply Now" })

  return t("join", { defaultValue: "Join" })
}

function getPrimaryPriceOption(offer: CoverPageOffer) {
  return offer.price_options[0]
}

function getOfferCohortId(offer: CoverPageOffer | null) {
  if (!offer) return null

  if (offer.kind === OfferKind.JoinCohort) return offer.cohort_id

  return null
}

function getOfferExternalUrl(offer: CoverPageOffer | null) {
  return (offer?.external_url || "").trim()
}

function getVisibilityLabel(
  source: { visibility?: number | null },
  t: CoverPageT
) {
  if (source.visibility === null || source.visibility === undefined) return null

  if (source.visibility >= 15)
    return t("panel_offer.public", { defaultValue: "Public" })
  if (source.visibility > 5)
    return t("panel_offer.members", { defaultValue: "Members" })

  return t("panel_offer.invite_only", { defaultValue: "Invite only" })
}

function getIconForVisibility(visibility?: number | null): IconName {
  if (visibility === null || visibility === undefined) return "lock"
  if (visibility >= 15) return "globe"
  if (visibility > 5) return "group"

  return "avatar-lock"
}

function getIconForCohort(cohort: CoverPageOfferableCohort): IconName {
  return getIconForVisibility(cohort.visibility)
}

function formatOfferDate(date: string | null | undefined) {
  if (!date) return null

  return new Intl.DateTimeFormat(undefined, {
    month: "numeric",
    day: "numeric",
    year: "numeric"
  }).format(new Date(date))
}

function getAvailabilityLabel({
  startsAt,
  endsAt,
  t
}: {
  startsAt?: string | null
  endsAt?: string | null
  t: CoverPageT
}) {
  const starts = formatOfferDate(startsAt)
  const ends = formatOfferDate(endsAt)

  if (starts && ends) return `${starts} - ${ends}`
  if (starts)
    return t("panel_offer.from_date", {
      defaultValue: "From {{date}}",
      date: starts
    })
  if (ends)
    return t("panel_offer.until_date", {
      defaultValue: "Until {{date}}",
      date: ends
    })

  return null
}

function getOfferAvailabilityLabels(offer: CoverPageOffer, t: CoverPageT) {
  const availability = getAvailabilityLabel({
    startsAt: offer.availability_starts,
    endsAt: offer.availability_ends,
    t
  })

  if (!availability)
    return [t("panel_offer.anytime", { defaultValue: "Anytime" })]

  return [availability]
}

function getOfferAccessLabel(offer: CoverPageOffer, t: CoverPageT) {
  const label = [
    getVisibilityLabel(offer, t),
    ...getOfferAvailabilityLabels(offer, t)
  ]
    .filter(Boolean)
    .join(", ")

  return label || null
}

function getCohortAccessLabel(cohort: GroupFragment, t: CoverPageT) {
  const visibility = getVisibilityLabel(cohort, t)
  const availability =
    getAvailabilityLabel({
      startsAt: cohort.registration_starts || cohort.access_starts,
      endsAt: cohort.registration_ends || cohort.access_ends,
      t
    }) ||
    (visibility ? t("panel_offer.anytime", { defaultValue: "Anytime" }) : null)
  const label = [visibility, availability].filter(Boolean).join(", ")

  return label || null
}

function stripHtml(html: string) {
  return html.replace(/<[^>]*>/g, "").trim()
}

function getOfferPricingLabel(offer: CoverPageOffer) {
  const priceOption = getPrimaryPriceOption(offer)

  return (
    stripHtml(
      priceOption?.price_description || priceOption?.option_label || ""
    ) || null
  )
}

function getCohortPricingLabel(cohort: GroupFragment, t: CoverPageT) {
  if (cohort.is_subscription_only)
    return t("panel_offer.subscription", { defaultValue: "Subscription" })
  if (typeof cohort.price === "number" && cohort.price > 0) {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: cohort.price % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(cohort.price)
  }
  if (cohort.price === 0 || cohort.is_subscription_only === false) {
    return t("panel_offer.free", { defaultValue: "Free" })
  }

  return null
}

export function buildOfferInitialValues(
  offer: CoverPageOffer | null,
  t: CoverPageT
): CoverPagePanelOfferValues {
  const kind = offer?.kind || OfferKind.JoinCohort
  let targetCohortId = ""

  if (offer && kind === OfferKind.JoinCohort) {
    targetCohortId = String(getOfferCohortId(offer) || "")
  }

  return {
    kind,
    targetCohortId,
    title: offer?.title || "",
    highlights: offer?.description || "",
    buttonLabel: offer ? offer.cta_label || "" : getDefaultButtonLabel(kind, t),
    accessLabel: offer ? getOfferAccessLabel(offer, t) || "" : "",
    pricingLabel: offer ? getOfferPricingLabel(offer) || "" : "",
    externalUrl: getOfferExternalUrl(offer)
  }
}

function OfferKindControl({
  disabled,
  kind
}: {
  disabled: boolean
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  const form = useFormikContext<CoverPagePanelOfferValues>()
  const offerKindOptions = useMemo(() => getOfferKindOptions(t), [t])

  return (
    <HStack align="center" gap={2} wrap="wrap">
      <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
        {t("panel_offer.grant_access_via", {
          defaultValue: "Grant access via:"
        })}
      </Text>
      <SelectField
        {...AUGMENTATION_PANEL_SELECT_PROPS}
        name="kind"
        label={t("panel_offer.offer_kind", { defaultValue: "Offer kind" })}
        hideLabel
        value={kind}
        disabled={disabled}
        rootProps={{ size: "sm", w: "auto", minW: "150px" }}
        options={offerKindOptions.map((option) => ({
          value: option.value,
          label: option.label
        }))}
        onChange={(event) => {
          const kind = event.target.value as OfferKind
          void form.setFieldValue("kind", kind)
          void form.setFieldValue("buttonLabel", getDefaultButtonLabel(kind, t))
        }}
      />
    </HStack>
  )
}

function OfferStaticPill({ value }: { value: string }) {
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      minH="32px"
      maxW="100%"
      px={3}
      borderRadius="full"
      bg="white"
      color="fg"
      fontSize="sm"
      fontWeight="bold"
      boxShadow="sm"
    >
      <Text as="span" m={0} truncate>
        {value}
      </Text>
    </Box>
  )
}

function ExistingOfferAccessSummary({
  offer,
  kind
}: {
  offer: CoverPageOffer
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  const label =
    kind === OfferKind.CreateCohort
      ? t("panel_offer.create_private_cohort_from", {
          defaultValue: "Create a private Cohort from"
        })
      : kind === OfferKind.External
      ? t("panel_offer.links_to", { defaultValue: "Links to" })
      : t("panel_offer.grants_access_to", {
          defaultValue: "Grants access to"
        })
  const value =
    kind === OfferKind.CreateCohort
      ? t("path_preview.source_path", { defaultValue: "Source Path" })
      : kind === OfferKind.External
      ? offer.external_url ||
        t("panel_offer.external_resource", {
          defaultValue: "External resource"
        })
      : offer.cohort_name || t("panel_offer.cohort", { defaultValue: "Cohort" })

  return (
    <HStack align="center" gap={2} wrap="wrap" minW={0}>
      <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>{label}</Text>
      <OfferStaticPill value={value} />
    </HStack>
  )
}

/**
 * A pill that leaves the panel for somewhere else in the app. It opens a new
 * tab: the offer being described here is half-filled in, and navigating away
 * from an unsaved form to look at what it points to would discard it.
 */
function OfferPillLink({
  icon,
  value,
  to,
  disabled = false
}: {
  icon: IconName
  value: string
  to?: string
  disabled?: boolean
}) {
  const content = (
    <>
      <Pathicon icon={icon} />
      {value}
    </>
  )

  if (!to || disabled) {
    return (
      <Button
        type="button"
        size="xs"
        borderRadius="full"
        variant="surface"
        alignSelf="flex-start"
        disabled
      >
        {content}
      </Button>
    )
  }

  return (
    <Button
      asChild
      type="button"
      size="xs"
      borderRadius="full"
      variant="surface"
      alignSelf="flex-start"
    >
      <Link
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        textDecoration="none"
      >
        {content}
      </Link>
    </Button>
  )
}

function OfferInfoPill({
  icon,
  value,
  tooltip
}: {
  icon: IconName
  value: string
  tooltip: string
}) {
  return (
    <Tooltip title={tooltip} trigger="click" content={{ maxW: "280px" }}>
      <Button
        type="button"
        size="xs"
        borderRadius="full"
        variant="surface"
        alignSelf="flex-start"
        aria-label={value}
      >
        <Pathicon icon={icon} />
        {value}
      </Button>
    </Tooltip>
  )
}

function OfferReadonlySetting({
  icon,
  label,
  value,
  ...linkProps
}: {
  icon: IconName
  label: string
  value: string
} & LinkProps) {
  return (
    <HStack align="center" gap={2} minW={0}>
      <Text as="span" {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
        {label}:
      </Text>
      <Link {...linkProps}>
        <HStack
          as="span"
          gap={1}
          minW={0}
          textStyle="augmentationPanel"
          textDecoration="none"
        >
          <Pathicon icon={icon} />
          <Text as="span" m={0} truncate>
            {value}
          </Text>
        </HStack>
      </Link>
    </HStack>
  )
}

function OfferHighlightsField({
  disabled,
  kind
}: {
  disabled: boolean
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  const placeholder = t("panel_offer.highlight_whats_included", {
    defaultValue:
      "List highlights of what’s included (videos, expert/teacher interactions, community, downloads,...)"
  })

  return (
    <CoverPagePanelRichTextField<CoverPagePanelOfferValues>
      name="highlights"
      label={t("panel_offer.highlights", { defaultValue: "Highlights" })}
      placeholder={placeholder}
      disabled={disabled}
    />
  )
}

function OfferPresentationFields({
  disabled,
  kind,
  offer
}: {
  disabled: boolean
  kind: OfferKind
  offer: CoverPageOffer | null
}) {
  const { t } = useCoverPageTranslate()
  return (
    <PanelSection.Root>
      <PanelSection.Heading>
        {t("panel_offer.presentation", { defaultValue: "Presentation" })}
      </PanelSection.Heading>
      <PanelSection.Content gap="15px">
        <Field
          {...AUGMENTATION_PANEL_INPUT_PROPS}
          name="title"
          label={t("panel_offer.title", { defaultValue: "Title" })}
          placeholder={
            kind === OfferKind.CreateCohort
              ? t("panel_offer.take_with_your_own_group", {
                  defaultValue: "Take with your own Group"
                })
              : t("panel_offer.title_placeholder", {
                  defaultValue:
                    "Enter an offer title (Self paced, Study along, Fall session...)"
                })
          }
          disabled={disabled}
        />
        <OfferHighlightsField disabled={disabled} kind={kind} />
        <Field
          {...AUGMENTATION_PANEL_INPUT_PROPS}
          name="buttonLabel"
          label={t("panel_offer.button_label", {
            defaultValue: "Button Label"
          })}
          placeholder={getDefaultButtonLabel(kind, t)}
          disabled={disabled}
        />
      </PanelSection.Content>
    </PanelSection.Root>
  )
}

function OfferAccessFields({
  offer,
  kind
}: {
  offer: CoverPageOffer | null
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  const form = useFormikContext<CoverPagePanelOfferValues>()
  const resource = useCoverPageContext((value) => value.resource)
  const selectedCohortId = Number(
    form.values.targetCohortId || offer?.cohort_id
  )
  // NOTE: maybe we should just add cohort to the offer schema and include it in our queries?
  const cohortQuery = useGroupQuery({
    variables: {
      id: selectedCohortId
    },
    skip: !selectedCohortId
  })
  const selectedCohort = cohortQuery.data?.group

  const accessLabel = selectedCohort
    ? getCohortAccessLabel(selectedCohort, t)
    : offer
    ? getOfferAccessLabel(offer, t)
    : form.values.accessLabel || null
  const pricingLabel = selectedCohort
    ? getCohortPricingLabel(selectedCohort, t)
    : offer
    ? getOfferPricingLabel(offer)
    : form.values.pricingLabel || null
  const offerCohortId = getOfferCohortId(offer)
  // The new-cohort target is a sentinel, not an id — there's no Cohort to open
  // until the Offer is added.
  const targetCohortId = isNewCohortTarget(form.values)
    ? null
    : selectedCohortId
  const groupSettingsUrl = offer
    ? `/manage/group/${resource.id}/${offerCohortId}/`
    : targetCohortId
    ? `/manage/group/${resource.id}/${targetCohortId}/`
    : `/manage/group/${resource.id}/new/`
  const commerceUrl = offer
    ? `/manage/group/${resource.id}/${offerCohortId}/commerce/`
    : targetCohortId
    ? `/manage/group/${resource.id}/${targetCohortId}/commerce/`
    : `/manage/group/${resource.id}/new/`
  const licensePricingUrl = `/manage/resource/${resource.id}/cohorts/license/pricing/`

  if (kind === OfferKind.External) return null
  if (!accessLabel && !pricingLabel) return null

  return (
    <PanelSection.Root>
      <PanelSection.Heading>
        {kind === OfferKind.JoinCohort
          ? t("panel_offer.cohort_access_pricing", {
              defaultValue: "Cohort Access & Pricing"
            })
          : t("panel_offer.access_pricing", {
              defaultValue: "Access & Pricing"
            })}
      </PanelSection.Heading>
      <PanelSection.Content gap={2}>
        {kind === OfferKind.JoinCohort ? (
          accessLabel ? (
            <OfferReadonlySetting
              icon="group"
              label={t("panel_offer.available_to", {
                defaultValue: "Available to"
              })}
              value={accessLabel}
              relativeTo={groupSettingsUrl}
            />
          ) : null
        ) : accessLabel ? (
          <OfferReadonlySetting
            icon="calendar"
            label={t("toolbar.access", { defaultValue: "Access" })}
            value={accessLabel}
            relativeTo={licensePricingUrl}
          />
        ) : null}
        {pricingLabel ? (
          <OfferReadonlySetting
            icon="credit-card"
            label={t("panel_offer.pricing", { defaultValue: "Pricing" })}
            value={pricingLabel}
            relativeTo={
              kind === OfferKind.JoinCohort ? commerceUrl : licensePricingUrl
            }
          />
        ) : null}
      </PanelSection.Content>
    </PanelSection.Root>
  )
}

export function OfferAccessTargetField({
  disabled,
  kind
}: {
  disabled: boolean
  kind: OfferKind
}) {
  const { t } = useCoverPageTranslate()
  const form = useFormikContext<CoverPagePanelOfferValues>()
  const resource = useCoverPageContext((value) => value.resource)
  const offerableCohortsQuery = useCoverPageOfferableCohortsQuery({
    variables: {
      resourceId: resource.id
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first"
  })
  const cohortOptions = useMemo(() => {
    return flattenEdges(offerableCohortsQuery.data?.groups) || []
  }, [offerableCohortsQuery.data?.groups])
  const isLoadingCohorts =
    offerableCohortsQuery.loading && !offerableCohortsQuery.data
  const sourcePathGroupId = resource.curriculum_group?.id
  const cohortTarget = resolveOfferCohortTarget({
    cohortCount: cohortOptions.length,
    currentTargetCohortId: form.values.targetCohortId,
    hasError: Boolean(offerableCohortsQuery.error),
    isLoading: offerableCohortsQuery.loading
  })
  const { hasNoAvailableCohorts } = cohortTarget
  const sourcePathUrl =
    sourcePathGroupId && resource.slug
      ? `/library/${resource.slug}/${sourcePathGroupId}/path/`
      : undefined

  useEffect(() => {
    if (kind !== OfferKind.JoinCohort) return

    if (cohortTarget.targetCohortId !== form.values.targetCohortId) {
      void form.setValues(
        {
          ...form.values,
          targetCohortId: cohortTarget.targetCohortId
        },
        false
      )
      return
    }

    if (hasNoAvailableCohorts) {
      void form.validateForm(form.values)
    }
  }, [
    cohortTarget.targetCohortId,
    form.setValues,
    form.validateForm,
    form.values,
    hasNoAvailableCohorts,
    kind
  ])

  if (kind === OfferKind.External) {
    return (
      <HStack align="center" gap={2} w="full">
        <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
          {t("panel_offer.to", { defaultValue: "To" })}:
        </Text>
        <Box flex="1" minW="180px">
          <Field
            {...AUGMENTATION_PANEL_INPUT_PROPS}
            as={Input}
            name="externalUrl"
            hideLabel
            label={t("panel_offer.external_url", {
              defaultValue: "External URL"
            })}
            placeholder="https://..."
            disabled={disabled}
          />
        </Box>
      </HStack>
    )
  }

  if (kind === OfferKind.CreateCohort) {
    return (
      <HStack align="center" gap={2} wrap="wrap">
        <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
          {t("header.from", { defaultValue: "From" })}:
        </Text>
        <OfferPillLink
          icon="path"
          value={t("path_preview.source_path", { defaultValue: "Source Path" })}
          to={sourcePathUrl}
          disabled={!sourcePathUrl}
        />
      </HStack>
    )
  }

  return (
    <VStack align="stretch" gap={2}>
      <HStack align="center" gap={2} wrap="wrap">
        <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
          {t("panel_offer.to", { defaultValue: "To" })}:
        </Text>
        {hasNoAvailableCohorts ? (
          <OfferInfoPill
            icon="plus"
            value={t("panel_offer.create_new_cohort", {
              defaultValue: "Create new cohort"
            })}
            tooltip={t("panel_offer.create_new_cohort_tooltip", {
              defaultValue:
                "Adding this Offer creates a Cohort with the same name. You can then set its price and access here."
            })}
          />
        ) : (
          <CustomSelect
            value={form.values.targetCohortId}
            disabled={disabled || isLoadingCohorts}
            placeholder={t("panel_offer.select_cohort", {
              defaultValue: "Select Cohort..."
            })}
            minW="180px"
            triggerProps={AUGMENTATION_PANEL_SELECT_PROPS}
            contentProps={{ borderRadius: "augmentationPanel" }}
            options={cohortOptions
              .map((cohort) => ({
                value: String(cohort.id),
                label:
                  cohort.name ||
                  t("panel_offer.cohort_with_number", {
                    defaultValue: "Cohort {{number}}",
                    number: cohort.id
                  }),
                icon: getIconForCohort(cohort)
              }))
              .concat([
                {
                  value: NEW_COHORT_TARGET_ID,
                  label: t("panel_offer.create_new_cohort", {
                    defaultValue: "Create new cohort"
                  }),
                  icon: "plus"
                }
              ])}
            onChange={(value) => {
              void form.setFieldValue("targetCohortId", value)
            }}
          />
        )}
      </HStack>
      {/* Picking an existing Cohort brings its access and pricing along, so the
          section below fills in and the choice speaks for itself. A Cohort that
          doesn't exist yet has nothing to fill it in with, so it says what
          adding the Offer will make instead. */}
      {!hasNoAvailableCohorts && isNewCohortTarget(form.values) ? (
        <Text {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}>
          {t("panel_offer.new_cohort_note", {
            defaultValue:
              "Adding this Offer creates a private Cohort named after it. Set its access and pricing from the Cohort afterward."
          })}
        </Text>
      ) : null}
    </VStack>
  )
}

function CoverPagePanelOfferForm({ offer }: { offer: CoverPageOffer | null }) {
  const { t } = useCoverPageTranslate()
  const getErrorMessage = useCoverPagePanelErrorMessage()
  const history = useHistory()
  const isEditable = useCoverPageIsEditable()
  const resource = useCoverPageContext((value) => value.resource)
  const refetchCoverPageOffers = useCoverPageContext(
    (value) => value.refetchCoverPageOffers
  )
  const scrollToSection = useCoverPageContext((value) => value.scrollToSection)
  const [createOffer] = useCreateOfferMutation()
  const [createCohort] = useCreateGroupMutation()
  const [updateOffer] = useUpdateOfferMutation()

  const initialValues = useMemo(
    () => buildOfferInitialValues(offer, t),
    [offer, t]
  )

  const validate = useMemo(() => createOfferValidator(t), [t])

  async function handleSubmit(
    values: CoverPagePanelOfferValues,
    form: FormikHelpers<CoverPagePanelOfferValues>
  ) {
    try {
      const kind = getCurrentOfferKind({
        offer,
        formKind: values.kind
      })
      let valuesForOffer = values

      if (
        !offer &&
        kind === OfferKind.JoinCohort &&
        isNewCohortTarget(values)
      ) {
        // First, create a new Cohort for this Offer.
        const createCohortResult = await createCohort({
          variables: {
            base_resource_id: resource.id,
            visibility: NEW_COHORT_VISIBILITY,
            // The new Cohort will share its name with the Offer title.
            name: values.title.trim(),
            role: null
          },
          update: (cache, { data }) => {
            const cohort = data?.createGroup
            if (cohort) {
              addCohortToOfferableCohortsCache(cache, resource.id, cohort)
            }
          }
        })
        const cohortId = createCohortResult.data?.createGroup?.id

        if (!cohortId) {
          throw new Error(
            t("panel_offer.create_cohort_failed", {
              defaultValue: "Cohort could not be created."
            })
          )
        }

        const targetCohortId = String(cohortId)
        valuesForOffer = {
          ...values,
          targetCohortId
        }
        await form.setFieldValue("targetCohortId", targetCohortId, false)
      }

      const offerInput = buildCoverPageOfferInput(
        valuesForOffer,
        kind,
        t
      ) as StoreOfferInput

      if (offer) {
        await updateOffer({
          variables: {
            resourceId: resource.id,
            id: offer.id,
            offer: offerInput
          }
        })
      } else {
        await createOffer({
          variables: {
            resourceId: resource.id,
            offer: offerInput
          },
          // Remove the related cohort from the offerable cohorts query.
          update: (cache, { data }) => {
            cache.updateQuery<
              CoverPageOfferableCohortsQuery,
              CoverPageOfferableCohortsQueryVariables
            >(
              {
                query: CoverPageOfferableCohortsDocument,
                variables: { resourceId: resource.id }
              },
              (offerableCohortsData) => {
                return offerableCohortsData?.groups
                  ? {
                      ...offerableCohortsData,
                      groups: {
                        ...offerableCohortsData?.groups,
                        edges:
                          offerableCohortsData?.groups?.edges.filter(
                            (edge) =>
                              edge.node.id !== data?.createOffer?.cohort_id
                          ) || []
                      }
                    }
                  : null
              }
            )
          }
        })
      }

      await refetchCoverPageOffers()

      if (!offer) {
        history.push(`/${COVER_PAGE_PANEL_ROOT_KEY}`)
        navigateToCoverPageOffersSection(scrollToSection)
      }
    } catch (error) {
      toaster.create({
        title: getErrorMessage(
          error,
          offer
            ? t("panel_errors.update_offer", {
                defaultValue: "Error updating Cover Page's offer:"
              })
            : t("panel_errors.create_offer", {
                defaultValue: "Error creating Cover Page's offer:"
              })
        ),
        type: "error",
        duration: 4000,
        closable: true
      })
      throw error
    }
  }

  return (
    <FormikWithDiscardFormState
      formStateKey={["cover-page", "offer", offer?.id || "new"]}
      initialValues={initialValues}
      validate={validate}
      validateOnMount
      enableReinitialize
      formStateOnSubmit={offer ? "preserve" : "clearOnSuccess"}
      onSubmit={handleSubmit}
    >
      {(form: FormikProps<CoverPagePanelOfferValues>) => {
        const disableFields = !isEditable || form.isSubmitting
        const kind = getCurrentOfferKind({
          offer,
          formKind: form.values.kind
        })

        const formId = "cover-page-panel-offer"

        return (
          <Form
            id={formId}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%"
            }}
          >
            <KeyboardFormSubmit />
            <VStack align="stretch" gap="15px" w="full">
              {offer ? (
                <ExistingOfferAccessSummary offer={offer} kind={kind} />
              ) : (
                <OfferKindControl disabled={disableFields} kind={kind} />
              )}
              <CoverPagePanelOfferCreateCohortGate
                gateEnabled={kind === OfferKind.CreateCohort}
              >
                {offer ? null : (
                  <OfferAccessTargetField
                    disabled={disableFields}
                    kind={kind}
                  />
                )}
                <PanelSection.Group>
                  <OfferPresentationFields
                    disabled={disableFields}
                    kind={kind}
                    offer={offer}
                  />
                  <OfferAccessFields offer={offer} kind={kind} />
                </PanelSection.Group>
              </CoverPagePanelOfferCreateCohortGate>
            </VStack>
            <CoverPagePanelFormActions
              form={form}
              formId={formId}
              disabled={!isEditable}
              submit={{
                children: offer
                  ? t("panel_offer.update_offer", {
                      defaultValue: "Update Offer"
                    })
                  : t("panel_offer.add_offer", { defaultValue: "Add Offer" })
              }}
              discard={{
                children: t("panel.discard", { defaultValue: "Discard" })
              }}
            />
          </Form>
        )
      }}
    </FormikWithDiscardFormState>
  )
}

export function buildCoverPageOfferInput(
  values: CoverPagePanelOfferValues,
  kind: OfferKind,
  t: CoverPageT
) {
  const offerInput: Record<string, unknown> = {
    title: values.title.trim(),
    description: values.highlights.trim(),
    cta_label: values.buttonLabel.trim() || null
  }

  if (kind === OfferKind.JoinCohort) {
    const cohortId = Number(values.targetCohortId)

    if (!cohortId) {
      throw new Error(
        t("panel_offer.select_a_cohort", { defaultValue: "Select a Cohort." })
      )
    }

    return {
      ...offerInput,
      kind: OfferKind.JoinCohort,
      cohort_id: cohortId
    }
  }

  if (kind === OfferKind.CreateCohort) {
    return {
      ...offerInput,
      kind: OfferKind.CreateCohort
    }
  }

  const externalUrl = values.externalUrl.trim()

  if (!externalUrl) {
    throw new Error(
      t("panel_offer.enter_a_link", { defaultValue: "Enter a link." })
    )
  }

  return {
    ...offerInput,
    kind: OfferKind.External,
    external_url: externalUrl
  }
}

export function CoverPagePanelOfferCreateRoute() {
  return <CoverPagePanelOfferForm offer={null} />
}

export const CoverPagePanelOfferRoute: AugmentationComponent<{
  offerId: string
}> = ({ route }) => {
  const offer = useCoverPageOffer(route?.match?.params?.offerId || "")

  if (!offer) {
    return (
      <Text fontSize="sm" color="blackAlpha.700" m={0}>
        Offer not found.
      </Text>
    )
  }

  return <CoverPagePanelOfferForm offer={offer} />
}
